import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import {
  getSettlementTypeLabel,
  normalizeSettlementType,
  SETTLEMENT_TYPE_PAYMENT,
} from "../utils/settlement";

export const LEDGER_WINDOW_DAYS = 7;
export const LEDGER_PAYMENT_MODES = ["Cash", "UPI", "Cheque", "Acc Transfer", "Card", "Razorpay"];
export const LEDGER_OPENING_BALANCE_CATEGORY = "Opening Balance";
export const LEDGER_OPENING_BALANCE_DATE = "1900-01-01";

const moneyFormatter = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function roundMoney(value) {
  return +Number(value || 0).toFixed(2);
}

export function formatLedgerMoney(value) {
  return moneyFormatter.format(Number(value) || 0);
}

export function localLedgerToday() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeWhatsAppPhoneCandidate(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (/^[6-9]\d{9}$/.test(digits)) return `91${digits}`;
  if (/^0[6-9]\d{9}$/.test(digits)) return `91${digits.slice(1)}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return digits;
  if (/^091[6-9]\d{9}$/.test(digits)) return digits.slice(1);
  if (/^0091[6-9]\d{9}$/.test(digits)) return digits.slice(2);
  if (digits.length >= 10 && digits.length <= 15) {
    return digits.startsWith("00") ? digits.slice(2) : digits;
  }
  return "";
}

export function extractLedgerWhatsAppPhone(value) {
  const text = String(value || "");
  const matches = text.match(/(?:\+?\d[\d\s().-]{7,}\d)/g) || [];
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const phone = normalizeWhatsAppPhoneCandidate(matches[index]);
    if (phone) return phone;
  }
  return "";
}

export function buildLedgerWhatsAppMessage(statement, receiptLink) {
  const partyLabel = statement?.partyLabel || "Party";
  return [
    "*ARF Seafoods*",
    `*${statement?.documentTitle || "Payment Ledger"}*`,
    `*${partyLabel}:* ${statement?.partyName || "-"}`,
    `*As on:* ${formatDisplayDate(statement?.asOfDate)}`,
    `*Pending Amount:* Rs. ${formatLedgerMoney(statement?.pendingAmount)}`,
    "",
    `View ledger: ${receiptLink}`,
  ].join("\n");
}

export function ledgerPartyName(type, entry) {
  if (type === "purchase") {
    return String(entry?.supplierName || "").trim();
  }
  const snapshot = String(entry?.customerName || "").trim();
  if (snapshot) return snapshot;
  return String(entry?.customer || "")
    .split(/\r?\n/)[0]
    .trim();
}

function entryDate(type, entry) {
  return toComparableYmd(type === "purchase" ? entry?.createdAt : entry?.saleDate);
}

export function filterMainLedgerEntries(type, entries) {
  const expectedCategory = type === "purchase" ? "General" : "Billing";
  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const category = String(entry?.category || (type === "purchase" ? "General" : "")).trim();
    return category === expectedCategory || category === LEDGER_OPENING_BALANCE_CATEGORY;
  });
}

export function isLedgerOpeningBalanceEntry(entry) {
  return String(entry?.category || "").trim() === LEDGER_OPENING_BALANCE_CATEGORY;
}

export function listLedgerParties(type, entries) {
  const parties = new Map();
  filterMainLedgerEntries(type, entries).forEach((entry) => {
    const name = ledgerPartyName(type, entry);
    if (!name) return;
    const key = name.toLocaleLowerCase();
    if (!parties.has(key)) parties.set(key, name);
  });
  return Array.from(parties.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

function itemDate(type, entry, item) {
  return toComparableYmd(type === "purchase" ? item?.date || entry?.createdAt : entry?.saleDate);
}

function makeReference(type, entry) {
  if (type === "sales") return String(entry?.billNo || "").trim();
  return String(entry?.id || "").slice(0, 8);
}

function buildTransactions(type, entries, asOfDate) {
  const transactions = [];
  let totalAmount = 0;
  let paidAmount = 0;
  let adjustmentAmount = 0;
  let entryCount = 0;

  entries.forEach((entry) => {
    const invoiceDate = entryDate(type, entry);
    if (!invoiceDate || invoiceDate > asOfDate) return;
    entryCount += 1;

    const entryTotal = roundMoney(entry?.total);
    totalAmount = roundMoney(totalAmount + entryTotal);
    const reference = makeReference(type, entry);
    const items = Array.isArray(entry?.items) ? entry.items : [];
    let grossItems = 0;

    items.forEach((item) => {
      const date = itemDate(type, entry, item) || invoiceDate;
      if (!date || date > asOfDate) return;
      const amount = roundMoney(item?.amount);
      const qty = Number(item?.qty) || 0;
      const rate = Number(item?.rate) || 0;
      if (amount === 0 && qty === 0) return;
      grossItems = roundMoney(grossItems + amount);
      transactions.push({
        date,
        kind: "item",
        sortOrder: 10,
        particulars: String(item?.material || (type === "purchase" ? "Material purchase" : "Sales item")),
        qty,
        rate,
        debit: amount,
        credit: 0,
        mode: "",
        reference,
      });
    });

    if (items.length === 0) {
      transactions.push({
        date: invoiceDate,
        kind: "item",
        sortOrder: 10,
        particulars: type === "purchase" ? "Material purchase" : `Sales invoice${reference ? ` ${reference}` : ""}`,
        qty: Number(entry?.totalQty) || 0,
        rate: 0,
        debit: entryTotal,
        credit: 0,
        mode: "",
        reference,
      });
      grossItems = entryTotal;
    }

    const invoiceDifference = roundMoney(grossItems - entryTotal);
    if (invoiceDifference > 0) {
      transactions.push({
        date: invoiceDate,
        kind: "invoice-adjustment",
        sortOrder: 20,
        particulars: type === "sales" ? "Invoice discount" : "Purchase adjustment",
        qty: 0,
        rate: 0,
        debit: 0,
        credit: invoiceDifference,
        mode: "Adjustment",
        reference,
      });
    } else if (invoiceDifference < 0) {
      transactions.push({
        date: invoiceDate,
        kind: "invoice-adjustment",
        sortOrder: 20,
        particulars: type === "sales" ? "Invoice adjustment" : "Purchase adjustment",
        qty: 0,
        rate: 0,
        debit: Math.abs(invoiceDifference),
        credit: 0,
        mode: "Adjustment",
        reference,
      });
    }

    const payments = Array.isArray(entry?.payments) ? entry.payments : [];
    let settledAmount = 0;
    payments.forEach((payment) => {
      const date = toComparableYmd(payment?.date || payment?.createdAt || payment?.created_at);
      const amount = roundMoney(payment?.amount);
      if (!date || date > asOfDate || amount <= 0) return;
      const settlementType = normalizeSettlementType(
        payment?.type || payment?.settlementType || payment?.transactionType
      );
      const isPayment = settlementType === SETTLEMENT_TYPE_PAYMENT;
      settledAmount = roundMoney(settledAmount + amount);
      if (isPayment) paidAmount = roundMoney(paidAmount + amount);
      else adjustmentAmount = roundMoney(adjustmentAmount + amount);
      transactions.push({
        date,
        kind: isPayment ? "payment" : "settlement-adjustment",
        sortOrder: isPayment ? 40 : 30,
        particulars: isPayment
          ? type === "purchase"
            ? "Payment paid"
            : "Payment received"
          : getSettlementTypeLabel(settlementType),
        qty: 0,
        rate: 0,
        debit: 0,
        credit: amount,
        mode: String(payment?.mode || (isPayment ? "Cash" : getSettlementTypeLabel(settlementType))).trim(),
        reference,
        createdAt: String(payment?.createdAt || payment?.created_at || ""),
      });
    });

    const isImplicitlyPaidSale =
      type === "sales" &&
      (entry?.paymentDone === true || Number(entry?.paymentDone) === 1) &&
      settledAmount === 0 &&
      entryTotal > 0;
    if (isImplicitlyPaidSale) {
      paidAmount = roundMoney(paidAmount + entryTotal);
      transactions.push({
        date: invoiceDate,
        kind: "payment",
        sortOrder: 40,
        particulars: "Payment received",
        qty: 0,
        rate: 0,
        debit: 0,
        credit: entryTotal,
        mode: String(entry?.paymentType || "Cash").trim(),
        reference,
        createdAt: invoiceDate,
      });
    }
  });

  return { transactions, totalAmount, paidAmount, adjustmentAmount, entryCount };
}

function groupTransactions(transactions) {
  const grouped = new Map();
  transactions.forEach((transaction) => {
    const groupableItem = transaction.kind === "item";
    const groupablePayment = transaction.kind === "payment" || transaction.kind === "settlement-adjustment";
    const key = groupableItem
      ? [transaction.date, transaction.kind, transaction.particulars.toLocaleLowerCase(), transaction.rate].join("|")
      : groupablePayment
        ? [transaction.date, transaction.kind, transaction.particulars, transaction.mode].join("|")
        : [transaction.date, transaction.kind, transaction.particulars, transaction.reference].join("|");
    if (!grouped.has(key)) {
      grouped.set(key, { ...transaction, references: new Set(transaction.reference ? [transaction.reference] : []) });
      return;
    }
    const current = grouped.get(key);
    current.qty += transaction.qty;
    current.debit = roundMoney(current.debit + transaction.debit);
    current.credit = roundMoney(current.credit + transaction.credit);
    if (transaction.reference) current.references.add(transaction.reference);
  });

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      qty: +Number(row.qty || 0).toFixed(3),
      debit: roundMoney(row.debit),
      credit: roundMoney(row.credit),
      references: Array.from(row.references),
    }))
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.particulars.localeCompare(b.particulars, undefined, { sensitivity: "base" });
    });
}

export function buildLedgerStatement({ type, entries, partyName, asOfDate = localLedgerToday() }) {
  const normalizedType = type === "sales" ? "sales" : "purchase";
  const normalizedAsOf = toComparableYmd(asOfDate) || localLedgerToday();
  const selectedParty = String(partyName || "").trim();
  const selectedKey = selectedParty.toLocaleLowerCase();
  const partyEntries = filterMainLedgerEntries(normalizedType, entries).filter(
    (entry) => ledgerPartyName(normalizedType, entry).toLocaleLowerCase() === selectedKey
  );
  const openingEntries = partyEntries.filter(isLedgerOpeningBalanceEntry);
  const activityEntries = partyEntries.filter((entry) => !isLedgerOpeningBalanceEntry(entry));
  const activity = buildTransactions(
    normalizedType,
    activityEntries,
    normalizedAsOf
  );
  const opening = buildTransactions(normalizedType, openingEntries, normalizedAsOf);
  const storedOpeningBalance = roundMoney(opening.totalAmount);
  const openingSettlements = opening.transactions.filter(
    (transaction) => transaction.kind === "payment" || transaction.kind === "settlement-adjustment"
  );
  const transactions = [...activity.transactions, ...openingSettlements];
  const totalAmount = roundMoney(activity.totalAmount + storedOpeningBalance);
  const paidAmount = roundMoney(activity.paidAmount + opening.paidAmount);
  const adjustmentAmount = roundMoney(activity.adjustmentAmount + opening.adjustmentAmount);
  const entryCount = activity.entryCount + opening.entryCount;
  const grouped = groupTransactions(transactions);
  const transactionDates = Array.from(new Set(grouped.map((row) => row.date).filter(Boolean))).sort();
  const selectedDates = transactionDates.slice(-LEDGER_WINDOW_DAYS);
  const selectedDateSet = new Set(selectedDates);
  const windowStart = selectedDates[0] || normalizedAsOf;
  const windowEnd = selectedDates[selectedDates.length - 1] || normalizedAsOf;
  const openingBalance = roundMoney(
    storedOpeningBalance +
      grouped
        .filter((row) => row.date < windowStart)
        .reduce((sum, row) => sum + row.debit - row.credit, 0)
  );
  let runningBalance = openingBalance;
  const rows = grouped
    .filter((row) => selectedDateSet.has(row.date))
    .map((row) => {
      runningBalance = roundMoney(runningBalance + row.debit - row.credit);
      return { ...row, runningBalance };
    });
  const closingBalance = roundMoney(
    storedOpeningBalance + grouped.reduce((sum, row) => sum + row.debit - row.credit, 0)
  );

  return {
    type: normalizedType,
    partyName: selectedParty,
    partyLabel: normalizedType === "purchase" ? "Supplier" : "Customer",
    documentTitle: normalizedType === "purchase" ? "Purchase Payment Ledger" : "Sales Payment Ledger",
    amountLabel: normalizedType === "purchase" ? "Purchase Amount" : "Sales Amount",
    paidLabel: normalizedType === "purchase" ? "Amount Paid" : "Amount Received",
    asOfDate: normalizedAsOf,
    windowStart,
    windowEnd,
    windowDateCount: selectedDates.length,
    storedOpeningBalance,
    openingBalance,
    closingBalance: Math.max(0, closingBalance),
    totalAmount,
    paidAmount,
    adjustmentAmount,
    pendingAmount: Math.max(0, closingBalance),
    entryCount,
    rows,
  };
}

export function buildPartyLedgerSummaries(type, entries, asOfDate = localLedgerToday(), additionalParties = []) {
  const parties = new Map();
  listLedgerParties(type, entries).forEach((partyName) => {
    parties.set(partyName.toLocaleLowerCase(), partyName);
  });
  (Array.isArray(additionalParties) ? additionalParties : []).forEach((party) => {
    const partyName = String(typeof party === "string" ? party : party?.name || "").trim();
    if (!partyName) return;
    const key = partyName.toLocaleLowerCase();
    if (!parties.has(key)) parties.set(key, partyName);
  });

  return Array.from(parties.values())
    .map((partyName) => buildLedgerStatement({ type, entries, partyName, asOfDate }))
    .sort((a, b) => {
      if (b.pendingAmount !== a.pendingAmount) return b.pendingAmount - a.pendingAmount;
      return a.partyName.localeCompare(b.partyName, undefined, { sensitivity: "base" });
    });
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatQty(value) {
  const number = Number(value) || 0;
  return number.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

export function buildLedgerPrintHtml(statement, logoUrl = "") {
  const isSalesStatement = statement.type === "sales";
  const printRows = statement.rows.reduce((groupedRows, row) => {
    const previousRow = groupedRows[groupedRows.length - 1];
    if (isSalesStatement && row.kind === "item") {
      if (previousRow?.kind === "sales-date-total" && previousRow.date === row.date) {
        previousRow.debit = roundMoney(previousRow.debit + Number(row.debit || 0));
        return groupedRows;
      }
      groupedRows.push({
        kind: "sales-date-total",
        date: row.date,
        debit: Number(row.debit || 0),
        credit: 0,
        particulars: "Sales amount",
      });
      return groupedRows;
    }

    if (row.kind === "item" && previousRow?.kind === "item-group" && previousRow.date === row.date) {
      previousRow.items.push(row);
      return groupedRows;
    }

    if (row.kind === "item") {
      groupedRows.push({
        kind: "item-group",
        date: row.date,
        items: [row],
      });
      return groupedRows;
    }

    groupedRows.push(row);
    return groupedRows;
  }, []);

  let previousPrintedDate = "";
  let dailyBalance = 0;
  const rows = printRows
    .map((row) => {
      const rowDate = String(row.date || "");
      const printedDate = rowDate !== previousPrintedDate ? formatDisplayDate(rowDate) : "";
      if (rowDate !== previousPrintedDate) dailyBalance = 0;
      previousPrintedDate = rowDate;

      if (isSalesStatement && row.kind === "sales-date-total") {
        dailyBalance = roundMoney(dailyBalance + Number(row.debit || 0));
        return `<tr class="sales-date-total">
          <td class="date-cell">${escapeHtml(printedDate)}</td>
          <td>Sales amount</td>
          <td class="number">${escapeHtml(formatLedgerMoney(row.debit))}</td>
          <td class="number balance-value">${escapeHtml(formatLedgerMoney(dailyBalance))}</td>
        </tr>`;
      }

      if (row.kind === "item-group") {
        const groupAmount = row.items.reduce((sum, item) => sum + Number(item.debit || 0), 0);
        dailyBalance = roundMoney(dailyBalance + groupAmount);
        const itemLines = row.items
          .map((item) => `<div class="item-line">${escapeHtml(item.particulars)}</div>`)
          .join("");
        const qtyLines = row.items
          .map((item) => `<div class="item-line">${escapeHtml(formatQty(item.qty))}</div>`)
          .join("");
        const rateLines = row.items
          .map((item) => `<div class="item-line">${item.rate ? escapeHtml(formatLedgerMoney(item.rate)) : ""}</div>`)
          .join("");
        const amountLines = row.items
          .map((item) => `<div class="item-line">${item.debit ? escapeHtml(formatLedgerMoney(item.debit)) : ""}</div>`)
          .join("");

        return `<tr class="item-group">
          <td class="date-cell">${escapeHtml(printedDate)}</td>
          <td>${itemLines}</td>
          <td class="number">${qtyLines}</td>
          <td class="number">${rateLines}</td>
          <td class="number">${amountLines}</td>
          <td class="number balance-value">${escapeHtml(formatLedgerMoney(dailyBalance))}</td>
        </tr>`;
      }

      dailyBalance = roundMoney(dailyBalance + Number(row.debit || 0) - Number(row.credit || 0));

      if (isSalesStatement) {
        return `<tr class="${escapeHtml(row.kind)}">
          <td class="date-cell">${escapeHtml(printedDate)}</td>
          <td>${escapeHtml(row.particulars)}</td>
          <td class="number${row.credit ? " payment-value" : ""}">${row.debit || row.credit ? escapeHtml(formatLedgerMoney(row.debit || row.credit)) : ""}</td>
          <td class="number balance-value">${escapeHtml(formatLedgerMoney(dailyBalance))}</td>
        </tr>`;
      }

      return `<tr class="${escapeHtml(row.kind)}">
        <td class="date-cell">${escapeHtml(printedDate)}</td>
        <td>${escapeHtml(row.particulars)}</td>
        <td class="number">${row.kind === "item" ? escapeHtml(formatQty(row.qty)) : ""}</td>
        <td class="number">${row.kind === "item" && row.rate ? escapeHtml(formatLedgerMoney(row.rate)) : ""}</td>
        <td class="number${row.credit ? " payment-value" : ""}">${row.debit || row.credit ? escapeHtml(formatLedgerMoney(row.debit || row.credit)) : ""}</td>
        <td class="number balance-value">${escapeHtml(formatLedgerMoney(dailyBalance))}</td>
      </tr>`;
    })
    .join("");

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      <title>${escapeHtml(statement.documentTitle)} - ${escapeHtml(statement.partyName)}</title>
      <style>
        @page { size: A4 portrait; margin: 9mm; }
        * { box-sizing: border-box; }
        body { margin: 0; color: #111827; background: #fff; font: 700 12.5px/1.35 Arial, sans-serif; }
        .statement { width: 100%; border: 1.4px solid #111827; }
        .header { display: grid; grid-template-columns: 62px 1fr 170px; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid #111827; }
        .logo { width: 58px; height: 58px; object-fit: contain; }
        .company { text-align: center; }
        .company h1 { margin: 0 0 3px; font-size: 25px; font-weight: 800; }
        .company div { line-height: 1.4; font-weight: 700; }
        .document { text-align: right; }
        .document h2 { margin: 0 0 6px; font-size: 17px; font-weight: 800; text-transform: uppercase; }
        .document div { margin-top: 3px; }
        .party { display: flex; justify-content: space-between; gap: 20px; padding: 10px 14px; border-bottom: 1px solid #111827; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        th, td { padding: 7px 5px; border-right: 1px solid #111827; border-bottom: 1px solid #cbd5e1; vertical-align: top; }
        th:last-child, td:last-child { border-right: 0; }
        th { border-bottom-color: #111827; background: #f1f5f9; font-size: 10.5px; font-weight: 800; text-align: left; text-transform: uppercase; }
        .date-cell { white-space: nowrap; }
        .number { text-align: right; }
        .item-line { min-height: 18px; }
        .item-line + .item-line { margin-top: 4px; }
        .previous-balance td, .closing td { font-weight: 800; background: #f8fafc; }
        .payment td { color: #065f46; }
        .payment-value { font-weight: 800; }
        .balance-value { font-weight: 800; }
        .footer { display: grid; grid-template-columns: 1fr 220px; min-height: 100px; }
        .note { padding: 10px 14px; line-height: 1.5; }
        .signature { display: flex; flex-direction: column; justify-content: space-between; padding: 10px 14px; border-left: 1px solid #111827; text-align: center; font-weight: 800; }
        @media screen { body { padding: 18px; background: #e5e7eb; } .statement { max-width: 980px; margin: auto; background: #fff; } }
        @media screen and (max-width: 640px) { body { padding: 0; background: #fff; } .statement { border: 0; } .header { grid-template-columns: 45px 1fr; } .document { grid-column: 1 / -1; text-align: left; } table { font-size: 10.5px; } th, td { padding: 6px 3px; overflow-wrap: anywhere; } }
      </style>
    </head>
    <body>
      <main class="statement">
        <header class="header">
          <div>${logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="ARF Seafoods logo" />` : ""}</div>
          <div class="company"><h1>ARF Seafoods</h1><div>Verkodu, Rameshwaram</div><div>9894286738</div></div>
          <div class="document"><h2>${escapeHtml(statement.documentTitle)}</h2><div>As on: ${escapeHtml(formatDisplayDate(statement.asOfDate))}</div><div>Latest ${LEDGER_WINDOW_DAYS} transaction dates</div></div>
        </header>
        <section class="party"><strong>${escapeHtml(statement.partyLabel)}: ${escapeHtml(statement.partyName)}</strong><span>Period: ${escapeHtml(formatDisplayDate(statement.windowStart))} to ${escapeHtml(formatDisplayDate(statement.windowEnd))}</span></section>
        <table>
          ${isSalesStatement
            ? '<thead><tr><th style="width:20%" class="date-cell">Date</th><th style="width:40%">Particulars</th><th style="width:20%" class="number">Amount</th><th style="width:20%" class="number">Balance</th></tr></thead>'
            : '<thead><tr><th style="width:15%" class="date-cell">Date</th><th style="width:31%">Particulars</th><th style="width:9%" class="number">Qty</th><th style="width:13%" class="number">Rate</th><th style="width:15%" class="number">Amount</th><th style="width:17%" class="number">Balance</th></tr></thead>'}
          <tbody>
            ${rows || `<tr><td colspan="${isSalesStatement ? 4 : 6}" style="padding:20px;text-align:center">No transactions up to the selected date.</td></tr>`}
            <tr class="closing"><td colspan="${isSalesStatement ? 3 : 5}" class="number">Closing Balance</td><td class="number">${escapeHtml(formatLedgerMoney(statement.pendingAmount))}</td></tr>
            <tr class="previous-balance"><td colspan="${isSalesStatement ? 3 : 5}" class="number">Previous Balance</td><td class="number balance-value">${escapeHtml(formatLedgerMoney(statement.openingBalance))}</td></tr>
          </tbody>
        </table>
        <footer class="footer"><div class="note">This statement shows item-wise activity for the latest seven transaction dates.</div><div class="signature"><div>For ARF Seafoods</div><div>Authorised Signature</div></div></footer>
      </main>
    </body>
  </html>`;
}
